/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import Modal from '../../../../components/Modal'
import { useTranslation } from 'react-i18next'
import TaskComplete, { TASK_ACTIONS } from '../../../../components/doc-preview/TaskComplete'
import type { Task } from '../../../../services/api/types'

type TaskActionType = (typeof TASK_ACTIONS)[keyof typeof TASK_ACTIONS]

interface TaskCompleteModalProps {
  task: any
  type?: TaskActionType
  onClose?: () => void
  onFinished?: () => void
  params?: Record<string, any>
}

/**
 * Modal wrapper for TaskComplete component
 * Displays approve/reject/reassign forms in a modal dialog
 * Note: This modal is managed by ModalContext, so show prop is always true
 */
export default function TaskCompleteModal({
  task,
  type = TASK_ACTIONS.taskComplete,
  onClose,
  onFinished,
  params
}: TaskCompleteModalProps) {
  const { t } = useTranslation()

  const getModalTitle = () => {
    switch (type) {
      case TASK_ACTIONS.taskReject:
        return t('TASKS_TASK_ACTIONS_REJECT_TASK')
      case TASK_ACTIONS.taskReassign:
        return t('TASKS_TASK_ACTIONS_REASSIGN_TASK')
      case TASK_ACTIONS.taskComplete:
      default:
        return t('TASKS_TASK_ACTIONS_APPROVE_TASK')
    }
  }

  const handleFinished = () => {
    if (onClose) {
      onClose()
    }
    if (onFinished) {
      onFinished()
    }
    window.dispatchEvent(new Event('updateDashboard'))
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <div className="complete-task-modal">
      <Modal show={true} handleClose={handleClose} title={getModalTitle()} size="md">
        <TaskComplete
          task={task}
          isModal={true}
          type={type}
          onFinished={handleFinished}
          onCancel={handleClose}
          params={params}
        />
      </Modal>
    </div>
  )
}
